import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, Search, Clear } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import TimeoffsTable from '../components/TimeoffsTable';
import TimeoffFormModal from '../components/TimeoffFormModal';
import TimeoffDeleteConfirmationDialog from '../components/TimeoffDeleteConfirmationDialog';
import TimeoffInfoDialog from '../components/TimeoffInfoDialog';
import Pagination from '../components/Pagination';
import { useTimeoffsApi } from '../hooks/useTimeoffsApi';
import { useHairdressersApi } from '../hooks/useHairdressersApi';
import { Timeoff, Hairdresser } from '../types/entities';
import { TimeoffFormData } from '../types/forms';

const TimeoffsPage: React.FC = () => {
  const { t } = useTranslation();
  const [timeoffs, setTimeoffs] = useState<Timeoff[]>([]);
  const [hairdressers, setHairdressers] = useState<Hairdresser[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Modal states
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [selectedTimeoff, setSelectedTimeoff] = useState<Timeoff | null>(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [hairdresserFilter, setHairdresserFilter] = useState<number | ''>('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  
  // Form data
  const [formData, setFormData] = useState<TimeoffFormData>({
    hairdresserId: 0,
    startDate: '',
    endDate: '',
    description: '',
  });

  const {
    error: timeoffsError,
    fetchTimeoffs,
    createTimeoff: createTimeoffApi,
    updateTimeoff: updateTimeoffApi,
    deleteTimeoff: deleteTimeoffApi,
  } = useTimeoffsApi();

  const {
    error: hairdressersError,
    fetchHairdressers,
  } = useHairdressersApi();

  const loadData = async () => {
    try {
      setInitialLoading(true);
      
      // Load hairdressers first
      const hairdressersData = await fetchHairdressers();
      setHairdressers(hairdressersData);
      
      // Set default hairdresser for form
      if (hairdressersData.length > 0 && formData.hairdresserId === 0) {
        setFormData(prev => ({
          ...prev,
          hairdresserId: hairdressersData[0].id,
        }));
      }
      
      // Load timeoffs
      const timeoffsData = await fetchTimeoffs();
      setTimeoffs(timeoffsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreateTimeoff = async (data: TimeoffFormData) => {
    try {
      const newTimeoff = await createTimeoffApi(data);
      setTimeoffs(prev => [newTimeoff, ...prev]);
      toast.success(t('timeoffs.timeoffCreated'));
      setOpenCreateModal(false);
    } catch (error) {
      console.error('Error creating timeoff:', error);
      toast.error('Failed to create time off');
    }
  };

  const handleUpdateTimeoff = async (data: TimeoffFormData) => {
    if (!selectedTimeoff) return;
    
    try {
      const updatedTimeoff = await updateTimeoffApi(selectedTimeoff.id, data);
      setTimeoffs(prev => prev.map(t => t.id === updatedTimeoff.id ? updatedTimeoff : t));
      toast.success(t('timeoffs.timeoffUpdated'));
      setOpenEditModal(false);
    } catch (error) {
      console.error('Error updating timeoff:', error);
      toast.error('Failed to update time off');
    }
  };

  const handleDeleteTimeoff = async () => {
    if (!selectedTimeoff) return;
    
    try {
      await deleteTimeoffApi(selectedTimeoff.id);
      setTimeoffs(prev => prev.filter(t => t.id !== selectedTimeoff.id));
      toast.success(t('timeoffs.timeoffDeleted'));
      setOpenDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting timeoff:', error);
      toast.error('Failed to delete time off');
    }
  };

  const handleEdit = (timeoff: Timeoff) => {
    setSelectedTimeoff(timeoff);
    setFormData({
      hairdresserId: timeoff.hairdresser?.id || 0,
      startDate: timeoff.startDate.toString(),
      endDate: timeoff.endDate.toString(),
      description: timeoff.description || '',
    });
    setOpenEditModal(true);
  };

  const handleDelete = (timeoff: Timeoff) => {
    setSelectedTimeoff(timeoff);
    setOpenDeleteDialog(true);
  };

  const handleModalClose = () => {
    setOpenCreateModal(false);
    setOpenEditModal(false);
    setOpenInfoDialog(false);
    setSelectedTimeoff(null);
    setFormData({
      hairdresserId: hairdressers.length > 0 ? hairdressers[0].id : 0,
      startDate: '',
      endDate: '',
      description: '',
    });
  };

  const handleInputChange = (field: keyof TimeoffFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setHairdresserFilter('');
    setStartDateFilter('');
    setEndDateFilter('');
  };

  const filteredTimeoffs = timeoffs.filter(timeoff => {
    const matchesSearch = !searchTerm || 
      timeoff.hairdresser?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      timeoff.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesHairdresser = !hairdresserFilter || timeoff.hairdresser?.id === hairdresserFilter;
    
    const matchesStartDate = !startDateFilter || 
      new Date(timeoff.startDate) >= new Date(startDateFilter);
    
    const matchesEndDate = !endDateFilter || 
      new Date(timeoff.endDate) <= new Date(endDateFilter);
    
    return matchesSearch && matchesHairdresser && matchesStartDate && matchesEndDate;
  });

  const hasActiveFilters = searchTerm || hairdresserFilter || startDateFilter || endDateFilter;

  if (initialLoading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ 
          mb: 3,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0,
          textAlign: isMobile ? 'center' : 'left'
        }}
      >
        <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{ mb: 0 }}>
          {t('timeoffs.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenCreateModal(true)}
          size={isMobile ? 'medium' : 'large'}
          fullWidth={isMobile}
        >
          {t('timeoffs.addTimeoff')}
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, alignItems: isMobile ? 'stretch' : 'center' }}>
          <TextField
            fullWidth={isMobile}
            placeholder={t('timeoffs.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchTerm('')} edge="end">
                    <Clear />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: isMobile ? '100%' : 300 }}
          />
          
          <FormControl sx={{ minWidth: isMobile ? '100%' : 200 }}>
            <InputLabel>{t('common.hairdresser')}</InputLabel>
            <Select
              value={hairdresserFilter}
              onChange={(e) => setHairdresserFilter(e.target.value as number | '')}
              label={t('common.hairdresser')}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {hairdressers.map((hairdresser) => (
                <MenuItem key={hairdresser.id} value={hairdresser.id}>
                  {hairdresser.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, alignItems: isMobile ? 'stretch' : 'center' }}>
          <TextField
            fullWidth={isMobile}
            type="date"
            label={t('timeoffs.fromDate')}
            value={startDateFilter}
            onChange={(e) => setStartDateFilter(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: isMobile ? '100%' : 200 }}
          />
          
          <TextField
            fullWidth={isMobile}
            type="date"
            label={t('timeoffs.toDate')}
            value={endDateFilter}
            onChange={(e) => setEndDateFilter(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: isMobile ? '100%' : 200 }}
          />
          
          {hasActiveFilters && (
            <Button
              variant="outlined"
              onClick={clearFilters}
              startIcon={<Clear />}
              sx={{ minWidth: isMobile ? '100%' : 'auto' }}
            >
              {t('timeoffs.clearAllFilters')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Error Display */}
      {(timeoffsError || hairdressersError) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {timeoffsError?.message || hairdressersError?.message || t('common.error')}
        </Alert>
      )}

      {/* Content */}
      {filteredTimeoffs.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {hasActiveFilters ? t('timeoffs.noTimeoffsMatching') : t('timeoffs.noTimeoffsFound')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {hasActiveFilters ? t('timeoffs.noTimeoffsMatchingMessage') : t('timeoffs.noTimeoffsMessage')}
          </Typography>
          {!hasActiveFilters && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpenCreateModal(true)}
            >
              {t('timeoffs.addNewTimeoff')}
            </Button>
          )}
        </Box>
      ) : (
        <>
          <TimeoffsTable
            timeoffs={filteredTimeoffs}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </>
      )}

      {/* Modals */}
      <TimeoffFormModal
        open={openCreateModal}
        onClose={handleModalClose}
        onSubmit={handleCreateTimeoff}
        title={t('timeoffs.addNewTimeoff')}
        submitButtonText={t('common.create')}
        hairdressers={hairdressers}
        initialData={formData}
      />

      <TimeoffFormModal
        open={openEditModal}
        onClose={handleModalClose}
        onSubmit={handleUpdateTimeoff}
        title={t('timeoffs.editTimeoff')}
        submitButtonText={t('common.save')}
        hairdressers={hairdressers}
        isEdit={true}
        initialData={formData}
      />

      <TimeoffDeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleDeleteTimeoff}
        timeoff={selectedTimeoff}
      />

      <TimeoffInfoDialog
        open={openInfoDialog}
        onClose={() => setOpenInfoDialog(false)}
        timeoff={selectedTimeoff}
      />
    </Container>
  );
};

export default TimeoffsPage;
